import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MEDIA, FALLBACK_DEMOS, getImageByPreviewId } from '../config/media';
import { useCMSData } from '../hooks/useCMSData';
import DemoPreviewModal from './DemoPreviewModal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiExternalLink, FiMaximize2 } = FiIcons;

const SamplePages = () => {
  const [activeDemo, setActiveDemo] = useState(null);
  const { previews } = useCMSData();

  // Use CMS previews if available, otherwise use fallback
  const demos = previews && previews.length > 0
    ? previews.map(p => normalizePreview(p))
    : FALLBACK_DEMOS;

  return (
    <section id="samples" className="py-20 bg-gradient-to-br from-black to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Page Examples
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            These are full, working examples of the pages we build. You can explore them here without leaving this page.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {demos.map((demo, index) => {
            // Get image: Sheet ImageURL first, then fallback by previewId, then by keywords, then emergency fallback
            const imageSrc = getPreviewImage(demo);
            const normalizedId = demo.id || normalizeId(demo.previewId || demo.label || '');

            return (
              <motion.div
                key={demo.id || index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="relative aspect-[9/16] max-w-[320px] mx-auto rounded-[2.5rem] border-[10px] border-white/10 overflow-hidden bg-black shadow-2xl transition-all duration-500 group-hover:-translate-y-4 group-hover:shadow-blue-500/20">
                  <img
                    src={imageSrc}
                    alt={demo.title || 'Trade landing page example'}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent flex flex-col justify-end p-8 text-center">
                    <p className="text-blue-400 font-black text-sm uppercase tracking-widest mb-2">
                      {getCardTitle(demo)}
                    </p>
                    <p className="text-slate-400 text-xs mb-6">
                      {getCardSubtitle(demo)}
                    </p>
                    <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-full py-2 px-6 inline-block mb-6">
                      <p className="text-slate-300 text-[11px] font-bold tracking-tight">
                        {getCardCaption(demo)}
                      </p>
                    </div>
                    <button
                      onClick={() => setActiveDemo(demo)}
                      className="w-full py-4 bg-white text-black font-black text-xs uppercase tracking-[0.2em] rounded-xl flex items-center justify-center gap-2 hover:bg-blue-500 hover:text-white transition-all transform group-hover:scale-105 mb-3"
                    >
                      View Page
                      <SafeIcon icon={FiExternalLink} />
                    </button>
                    <Link
                      to={`/preview/${normalizedId}`}
                      className="w-full py-2.5 bg-white/5 text-slate-400 font-semibold text-[10px] uppercase tracking-widest rounded-xl flex items-center justify-center gap-2 hover:bg-white/10 hover:text-white transition-all border border-white/10"
                    >
                      Open Page
                      <SafeIcon icon={FiMaximize2} className="w-3 h-3" />
                    </Link>
                    <p className="text-[10px] text-slate-500 mt-3 italic">
                      Watch how it reshapes for your screen.
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <DemoPreviewModal
        isOpen={!!activeDemo}
        onClose={() => setActiveDemo(null)}
        demoUrl={getPreviewUrl(activeDemo)}
        demoTitle={activeDemo?.title || activeDemo?.label}
      />
    </section>
  );
};

// Normalize CMS preview data to consistent format
const normalizePreview = (p) => ({
  id: normalizeId(p.previewId || p.PreviewID || p.id || ''),
  previewId: p.previewId || p.PreviewID,
  imageUrl: p.imageUrl || p.ImageURL,
  previewUrl: p.previewUrl || p.PreviewURL || p.url,
  buttonText: p.buttonText || p.ButtonText,
  title: p.title || p.Title,
  subtitle: p.subtitle || p.Subtitle,
  description: p.description || p.Description,
  industry: p.industry || p.Industry,
  location: p.location || p.Location,
  label: p.label || p.industry || p.Title || 'Trade Page',
  sortOrder: p.sortOrder || p.SortOrder,
  active: p.active ?? p.Active
});

// Normalize ID for matching: lowercase, trim, replace spaces/underscores with hyphens
const normalizeId = (id) => {
  if (!id) return '';
  return id
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

// Get preview image with proper fallback priority
const getPreviewImage = (demo) => {
  // Priority 1: Sheet ImageURL if provided and not blank
  if (demo.imageUrl && demo.imageUrl.trim() !== '') {
    return demo.imageUrl;
  }

  // Priority 2: Original image mapping by previewId
  const byId = getImageByPreviewId(demo.id);
  if (byId) return byId;

  // Priority 3: Match by title/label/industry keywords
  const searchText = `${demo.title || ''} ${demo.industry || ''} ${demo.label || ''}`.toLowerCase();
  if (searchText.includes('electric')) return MEDIA.DEMO_ELECTRICIAN.src;
  if (searchText.includes('detail')) return MEDIA.DEMO_DETAILING.src;
  if (searchText.includes('landscap') || searchText.includes('mow')) return MEDIA.DEMO_LANDSCAPING.src;
  if (searchText.includes('roof')) return MEDIA.DEMO_ROOFING.src;
  if (searchText.includes('pressure') || searchText.includes('powerwash')) return MEDIA.DEMO_PRESSURE_WASH.src;
  if (searchText.includes('clean')) return MEDIA.DEMO_CLEANING.src;

  // Priority 4: Emergency fallback
  return MEDIA.DEMO_ELECTRICIAN.src;
};

// Get card title with proper fallback priority
const getCardTitle = (demo) => {
  return demo.title || demo.industry || demo.label || 'Trade Page';
};

// Get card subtitle with proper fallback priority
const getCardSubtitle = (demo) => {
  return demo.subtitle || demo.location || demo.label || '';
};

// Get card caption with proper fallback priority
const getCardCaption = (demo) => {
  return demo.description || 'Built using BPA Trade Pages framework';
};

// Get preview URL with proper fallback priority
const getPreviewUrl = (demo) => {
  return demo?.previewUrl || demo?.url || null;
};

export default SamplePages;